import type { HrisIntegration, HrisIntegrationType } from "./models";

export type PaycomSftpDepartmentIdParts = {
	baseDepartmentId: string;
	alSuffix: string;
};

const PAYCOM_SFTP_AL_SUFFIX_PATTERN = /^\d+$/;
const PAYCOM_SFTP_DEPARTMENT_ID_PATTERN = /^(.*)_(\d+)$/;

/**
 * Splits Paycom SFTP's optional AL suffix from the final numeric segment only.
 *
 * The base match is deliberately greedy: `A_B_800` is base `A_B` plus suffix `800`, while a
 * non-numeric trailing segment such as `A_B_AL` remains part of the base unchanged.
 */
export const splitPaycomSftpDepartmentId = (departmentId: string): PaycomSftpDepartmentIdParts => {
	const match = PAYCOM_SFTP_DEPARTMENT_ID_PATTERN.exec(departmentId);
	if (match === null) return { baseDepartmentId: departmentId, alSuffix: "" };

	return { baseDepartmentId: match[1], alSuffix: match[2] };
};

export const isValidPaycomSftpAlSuffix = (alSuffix: string): boolean => alSuffix === "" || PAYCOM_SFTP_AL_SUFFIX_PATTERN.test(alSuffix);

/** Rebuilds the one composite ID persisted in `hris_department_id`. */
export const composePaycomSftpDepartmentId = (baseDepartmentId: string, alSuffix: string): string => {
	if (alSuffix === "") return baseDepartmentId;
	if (baseDepartmentId === "") throw new Error("Paycom SFTP base department ID is required when an AL suffix is present");
	if (!isValidPaycomSftpAlSuffix(alSuffix)) throw new Error("Paycom SFTP AL suffix must contain only digits");

	return `${baseDepartmentId}_${alSuffix}`;
};

const ADP_DEPARTMENT_KEY_SEPARATOR = ";";

/**
 * ADP routes one department on several keys joined by ";" (a building's Location Code plus any
 * `<code>_<home department>` it also takes). Picking a key from the file therefore adds it to the list
 * rather than replacing what is configured; a key already present (case-insensitively, as the sync
 * compares them) is not added twice.
 */
export const addAdpDepartmentKey = (currentDepartmentId: string, pickedKey: string): string => {
	const keys = currentDepartmentId
		.split(ADP_DEPARTMENT_KEY_SEPARATOR)
		.map((key) => key.trim())
		.filter((key) => key !== "");
	const picked = pickedKey.trim();
	if (picked === "" || keys.some((key) => key.toLowerCase() === picked.toLowerCase())) return keys.join(ADP_DEPARTMENT_KEY_SEPARATOR);
	return [...keys, picked].join(ADP_DEPARTMENT_KEY_SEPARATOR);
};

type WithHrisIntegrations = { hrisIntegrations?: HrisIntegration[] };

/**
 * Whether a department has at least one HRIS integration of the given type.
 * Avoids the unsafe `hrisIntegrations.at(0)?.source` shortcut: a department can have
 * multiple integrations across types and multiple sources for the same type.
 */
export const hasHrisIntegrationOfType = (department: WithHrisIntegrations | undefined, type: HrisIntegrationType): boolean =>
	department?.hrisIntegrations?.some((i) => i.type === type) ?? false;

/**
 * The longest `folder`/`file` we accept, mirroring the backend's `_MAX_SFTP_NAME_LENGTH`.
 * An S3 key maxes out at 1024 bytes and the two are joined into one, so half each is the ceiling.
 */
const MAX_SFTP_NAME_LENGTH = 512;

// The client half of the backend's `_SFTP_SEGMENT` allowlist (`services/integrations.py`): one path
// segment, opening with a word character — which is what makes `.`/`..` traversal and the `.DS_Store`
// junk in the bucket unmatchable — and no separator of any kind. `&` and `()` are in the class because
// vendors really send them (the live `Employee Punch - 4091BEL & 4091ENG.xlsx` feed, Empeon's `(8)`
// duplicate suffixes). Written with Unicode property escapes rather than `\w`, which is ASCII-only in
// JS while Python's is not: refusing an accented filename the backend would accept is the same
// offer-then-reject split the backend comment warns about, just one layer earlier.
const SFTP_NAME_CHARACTER = String.raw`[\p{L}\p{N}\p{M}_.\-&()]`;
const SFTP_NAME_OPENING_CHARACTER = String.raw`[\p{L}\p{N}\p{M}_]`;
const SFTP_FILE_NAME_PATTERN = new RegExp(`^${SFTP_NAME_OPENING_CHARACTER}${SFTP_NAME_CHARACTER}*(?: ${SFTP_NAME_CHARACTER}+)*$`, "u");

/**
 * Whether `name` is a filename a feed may be pointed at — the client mirror of the backend's
 * `is_valid_sftp_segment`, so a typed name is refused where it is typed rather than as a 400 on save.
 *
 * Deliberately says nothing about whether a file by that name exists. A configured `file` is matched
 * as a PREFIX by the sync, which is how one feed reads both `EmployeeList400am.csv` and
 * `EmployeeList800am.csv`, and a feed is often configured before its first delivery lands.
 */
export const isValidSftpFileName = (name: string): boolean => name.length <= MAX_SFTP_NAME_LENGTH && SFTP_FILE_NAME_PATTERN.test(name);

/**
 * Which of a folder's delivered files a configured `file` would select, newest-first order preserved.
 *
 * Mirrors `S3FileSyncher._resolve_files_by_prefix`: the configured name is joined onto the folder and
 * matched as a prefix, so an exact name yields the one file and a shortened one yields every delivery
 * sharing it. Used to show, before saving, that `EmployeeList` really does reach both of Atlantis'
 * morning drops — the question a free-text name exists to answer.
 */
export const sftpFilesMatchedByName = (deliveredNames: string[], name: string): string[] =>
	name ? deliveredNames.filter((delivered) => delivered.startsWith(name)) : [];
